import api from '@/api'
import Vue from "vue";
import router from "@/routes";

function getTokenPayload(token) {
  try {
    if (typeof token === 'string') {
      let data = token.split('.');
      if (data.length === 3) {
        return JSON.parse(atob(data[1]));
      }
    }
    return null;
  }
  catch(err) {
    return null
  }
}

const state = {
  session_token_value: localStorage.getItem('epp_session_token_value'),
  session_token_header: localStorage.getItem('epp_session_token_header'),
  login_token_value: localStorage.getItem('epp_login_token_value'),
  login_token_header: localStorage.getItem('epp_login_token_header'),
  session_expires: -1,
  login_expires: -1,
  logged_in: false,
  is_admin: false,
  session_watcher: null,
};

if (state.login_token_value !== null) {
  let payload = getTokenPayload(state.login_token_value);
  if (payload !== null) {
    state.login_expires = payload.exp || state.login_expires;
  }
}

const getters = {

  isAuthed(state) {
    return state.logged_in
  },

  isAdmin(state) {
    return state.is_admin;
  },

  isSessionActive(state, getters) {
    return getters.getSessionTimeLeft > 0
  },

  isLoginActive(state, getters) {
    return getters.getLoginTimeLeft > 0
  },

  getSessionTimeLeft(state, getters) {
    let exp = getters.getSessionExpires;
    if (exp < 1) {
      return -1
    }
    return exp - Math.floor(Date.now() / 1000);
  },

  getLoginTimeLeft(state, getters) {
    let exp = getters.getLoginExpires;
    if (exp < 1) {
      return -1
    }
    return exp - Math.floor(Date.now() / 1000);
  },

  getLoginToken(state) {
    return state.login_token_value
  },

  getLoginTokenHeader(state) {
    return state.login_token_header
  },

  getSessionToken(state) {
    return state.session_token_value
  },

  getSessionTokenHeader(state) {
    return state.session_token_header
  },

  getLoginExpires(state) {
    return state.login_expires
  },

  getSessionExpires(state) {
    return state.session_expires
  },

  getSessionWatcher(state) {
    return state.session_watcher
  },

  isSessionWatcherRunning(state, getters) {
    return getters.getSessionWatcher !== null
  },

  getUserId(state, getters) {
    let payload = getTokenPayload(getters.getLoginToken);
    if (payload !== null && payload.user_id) {
      return payload.user_id;
    }
    return 0;
  },

  getCredit(state, getters) {
    let payload = getTokenPayload(getters.getSessionToken);
    if (payload !== null && payload.credit) {
      return parseInt(payload.credit);
    }
    return 0;
  }

};

const mutations = {
  authenticate(state, data) {
    Vue.set(state, 'login_token_value', data.login_token);
    Vue.set(state, 'login_token_header', data.login_header);
    Vue.set(state, 'login_expires', data.login_payload.exp);

    localStorage.setItem("epp_login_token_value", data.login_token);
    localStorage.setItem("epp_login_token_header", data.login_header);
  },

  continueSession(state, data) {
    Vue.set(state, 'session_token_value', data.session_token);
    Vue.set(state, 'session_token_header', data.session_header);

    localStorage.setItem("epp_session_token_value", data.session_token);
    localStorage.setItem("epp_session_token_header", data.session_header);

    api.setSession(data.session_header, data.session_token);

    Vue.set(state, 'logged_in', true);

    let is_admin = ('roles' in data.session_payload && data.session_payload.roles.indexOf('admin') >= 0);
    Vue.set(state, 'is_admin', is_admin);
    Vue.set(state, 'session_expires', data.session_payload.exp);
  },

  anonymize(state) {
    Vue.set(state, 'session_token_value', null);
    Vue.set(state, 'session_token_header', null);
    Vue.set(state, 'login_token_value', null);
    Vue.set(state, 'login_token_header', null);
    Vue.set(state, 'session_expires', -1);
    Vue.set(state, 'login_expires', -1);
    Vue.set(state, 'logged_in', false);
    Vue.set(state, 'is_admin', false);

    api.setSession(null, null);

    let sW = state.session_watcher;
    Vue.set(state, 'session_watcher', null);

    if (sW !== null) {
      clearTimeout(sW);
    }

    localStorage.removeItem("epp_login_token_value");
    localStorage.removeItem("epp_login_token_header");
    localStorage.removeItem("epp_session_token_value");
    localStorage.removeItem("epp_session_token_header");
  },

  setSessionWatcher(state, value) {
    Vue.set(state, 'session_watcher', value);
  }

};

const actions = {
  login(ctx, request) {
    api.logIn(
      request,
      data => {
        data.login_payload = getTokenPayload(data.login_token);
        if (data.login_payload === null) {
          ctx.dispatch('messages/addErrorMessage', 'Invalid JSON Web Token, cannot authenticate.', {root: true});
          return;
        }

        data.session_payload = getTokenPayload(data.session_token);
        if (data.session_payload === null) {
          ctx.dispatch('messages/addErrorMessage', 'Invalid JSON Web Token, cannot authenticate.', {root: true});
          return;
        }

        ctx.commit('authenticate', data);
        ctx.commit('continueSession', data);
        ctx.dispatch('watchSession', true);
        ctx.dispatch('messages/addStatusMessage', 'You have been successfully logged in.', {root: true});

        router.push({name: 'admin'});
      }
    )
  },

  logout(ctx) {
    ctx.commit('anonymize');
    ctx.dispatch('messages/addStatusMessage', 'You have been logged out.', {root: true});
    router.push({name: 'overview'});
  },

  watchSession(ctx, force) {
    if (ctx.getters.getSessionWatcher !== null) {
      if (force === true) {
        clearTimeout(ctx.getters.getSessionWatcher);
        ctx.commit('setSessionWatcher', null);
      } else {
        return;
      }
    }

    // If session expires in less than 5 seconds or login expires before session, log the user out.
    if (ctx.getters.getSessionTimeLeft < 5 || ctx.getters.getLoginTimeLeft <= ctx.getters.getSessionTimeLeft) {
      ctx.dispatch('messages/addWarningMessage', "Your session has expired. In order to continue, please <a href=\"/login\">log in</a>.", {root: true});
      ctx.dispatch('logout');
      return;
    }

    let id = setTimeout(() => {
      ctx.dispatch('extendSession');
      ctx.commit('setSessionWatcher', null);
    }, ctx.getters.getSessionTimeLeft * 1000);

    ctx.commit('setSessionWatcher', id);
  },

  extendSession(ctx) {
    let token = ctx.getters.getLoginToken;
    let header = ctx.getters.getLoginTokenHeader;

    if (typeof token !== 'string' || token === '') {
      return;
    }

    if (typeof header !== 'string' || header === '') {
      return;
    }

    api.reloadSession(
      {login_token: token},
      data => {
        data.session_payload = getTokenPayload(data.session_token);
        if (data.session_payload === null) {
          ctx.dispatch('messages/addErrorMessage', 'Invalid JSON Web Token, cannot authenticate.', {root: true});
          ctx.dispatch('logout');
          return;
        }
        ctx.commit('continueSession', data);
        ctx.dispatch('watchSession', true);
      },
      error => {
        ctx.dispatch('messages/httpErrorMessage', error, {root: true});
        ctx.dispatch('logout')
      }
    );
  },

};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/auth.js